import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFlag } from 'flags';
import { debounce } from 'lodash';

import Button from 'components/Button/Button';
import Filter from 'components/Filter/Filter';
import Tooltip from 'components/Tooltip/Tooltip';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import DeleteTemplate from './Modals/Delete';
import LoadingDuplicatingModalContent from './Modals/LoadingModal';
import { duplicateTemplate } from 'helpers/canvasHelpers';
import { defaultModal, openModal, showModal } from 'helpers/modals';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './Templates.scss';

import templateSrc from './templateHelpers';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Progress,
  Flex,
} from '@chakra-ui/react';
import DesignImporter from './Modals/DesignImporter/DesignImporter';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useCreateDoubleSidedTemplateMutation,
  useDeleteTemplateByIdMutation,
} from 'modules/templates/templatesApi';

const Templates = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const [warningModal, setWarningModal] = useState(defaultModal);
  const [openDesignImporterModal, setOpenDesignImporterModal] = useState(false);
  const [refreshSymbol, setRefreshSymbol] = useState(Symbol());
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const {
    data: templatesData,
    error,
    isLoading,
    isFetching,
  } = useGetTemplatesQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
  });

  const [deleteTemplateById] = useDeleteTemplateByIdMutation();
  const [createTemplate] = useCreateTemplateMutation();
  const [createDoubleSidedTemplate] = useCreateDoubleSidedTemplateMutation();

  const handleDuplicateTemplate = async templateData => {
    const context = {
      createTemplate,
      createDoubleSidedTemplate,
      navigate,
    };

    try {
      await duplicateTemplate(templateData, context);
      setWarningModal(defaultModal);
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Template duplicate template successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to duplicate a Template',
        })
      );
    }
  };

  const handleDeleteTemplate = async id => {
    try {
      await deleteTemplateById(id).unwrap(); // Call the mutation and unwrap the response
      setWarningModal(defaultModal);
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Template removed successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to remove a Template',
        })
      );
    }
  };

  const columns = [
    {
      accessorKey: '',
      header: 'Preview',
      enableSorting: false,
      cell: ({ row }) => {
        const template = row.original;
        const bg_img = row.original.bg_img;
        const double_sided = row.original.double_sided;
        const ad_template_pages = row.original.ad_template_pages;

        return (
          <>
            {!bg_img && double_sided === false && (
              <i className="fas fa-image noThumb" />
            )}

            {double_sided === true &&
              !ad_template_pages[0]?.bg_img &&
              !ad_template_pages[1]?.bg_img && (
                <i className="fas fa-image noThumb" />
              )}

            {bg_img && (
              <img src={templateSrc(template, 'thumb')} alt={template.name} />
            )}

            {(ad_template_pages[0]?.bg_img || ad_template_pages[1]?.bg_img) && (
              <img src={templateSrc(template, 'thumb')} alt={template.name} />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: props => <Text className="name-test">{props.getValue()}</Text>,
    },
    {
      accessorKey: 'campaigns_count',
      header: 'Associations',
      id: 'associations',
      enableSorting: false,
      textAlign: 'center',
      cell: props => {
        return (
          <Text>
            {props.getValue() > 0
              ? `Used in ${props.getValue()} campaign${
                  props.getValue() > 1 ? 's' : ''
                } ...`
              : '--'}
          </Text>
        );
      },
    },

    {
      accessorKey: '',
      header: 'Actions',
      id: 'actions',
      enableSorting: false,
      textAlign: 'center',
      minWidth: '120px',
      cell: props => {
        const { id, name } = props.row.original;
        return (
          <Box className="templates-actions" style={{ paddingLeft: '0.5rem' }}>
            <Tooltip
              tipText={'Duplicate template.'}
              direction="left"
              render={() => (
                <Button
                  icon="copy"
                  iconColor={colors.slate}
                  btnStyle="transparent"
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    const clickedId = Number(
                      event.currentTarget.getAttribute('data-id')
                    );

                    if (clickedId === id) {
                      openModal(
                        setWarningModal,
                        id,
                        'Duplicating Template',
                        <LoadingDuplicatingModalContent />
                      );
                      handleDuplicateTemplate(props.row.original);
                    }
                  }}
                  data-id={id}
                />
              )}
            />
            <Tooltip
              tipText={
                props.getValue()
                  ? 'Detach template from all campaigns to delete.'
                  : 'Delete template.'
              }
              direction="left"
              render={() => (
                <Button
                  icon="delete"
                  iconColor={colors.slate}
                  btnStyle="transparent"
                  disabled={props.getValue()}
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    const clickedId = Number(
                      event.currentTarget.getAttribute('data-id')
                    );

                    if (!props.getValue() && clickedId === id) {
                      openModal(
                        setWarningModal,
                        id,
                        'Template Delete',
                        <DeleteTemplate
                          callback={handleDeleteTemplate}
                          name={name}
                          id={id}
                        />
                      );
                    }
                  }}
                  data-id={id}
                />
              )}
            />
          </Box>
        );
      },
    },
  ];

  const table = useReactTable({
    data: templatesData?.data.entities || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // onSortingChange: setSorting,
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
      // sorting,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Templates',
      })
    );
  }

  // Handle rows per clicked
  const handleRowClick = row => {
    const templateId = row.original.id;
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
    if (!isReadOnly) {
      navigate(
        `/template-editor${
          templateId !== 0 ? '?id=' + templateId : ''
        }&ref=/templates`
      );
    }
  };

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'asc',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'desc',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [],
      });
    }
  };

  return (
    <>
      <Box className="templates-header">
        {!isReadOnly && (
          <Flex>
            <Box className="templates-header__left">
              <Button
                type="button"
                icon="add"
                text="New Template"
                textColor={colors.slate}
                btnStyle="transparent"
                url="/template-editor?ref=/templates"
              />
            </Box>
            <Box className="templates-header__left">
              <Button
                type="button"
                icon="add"
                text="Import Template"
                textColor={colors.slate}
                btnStyle="transparent"
                onClick={() => setOpenDesignImporterModal(true)}
              />
            </Box>
          </Flex>
        )}
        <Box className="templates-header__nav"></Box>
        <Box className="templates-header__right">
          <Box className="templates-header__status">
            Currently used in campaign(s).
          </Box>
        </Box>
      </Box>

      <Box className="templates-body">
        <Box height="60px" className="rail rail-rule">
          <Filter
            value={searchInput}
            placeholder="Search templates"
            onChange={event => {
              setSearchInput(event); // Update raw input immediately
              handleDebouncedSearch(event); // Trigger debounced update
            }}
          />
        </Box>

        <Box className="templates-table" borderWidth="1px">
          <TableContainer>
            {(isLoading || isFetching) && (
              <Progress size="xs" isIndeterminate />
            )}
            <Table size="md" variant="simple">
              <Thead zIndex={1} bg="white">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr className="templates-table__row" key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const textAlign =
                        header.column.columnDef.textAlign || 'left';
                      const minWidth = header.column.columnDef.minWidth || null;
                      return (
                        <Th
                          className="templates-table__header"
                          key={header.id}
                          whiteSpace="nowrap"
                          textAlign={textAlign}
                          minWidth={minWidth}
                        >
                          {header.column.getCanSort() ? (
                            <Flex justify={textAlign} align="center">
                              <Text
                                className="templates-table__header-text"
                                mr={2}
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Text>

                              <Box
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {defaultSearchParameters.sort[0]?.field ===
                                header.id ? (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={
                                      defaultSearchParameters.sort[0]
                                        ?.direction || 'asc'
                                    }
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                ) : (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={'normal'}
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                )}
                              </Box>
                            </Flex>
                          ) : (
                            <Text
                              className="templates-table__header-text"
                              mr={2}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <Tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      bg={row.id === selectedRowId ? 'gray.100' : 'transparent'}
                      color={colors.slate}
                      _hover={{
                        bg: colors.lighterGrey,
                        textColor: colors.deepSlate2,
                      }}
                      cursor="pointer"
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const { campaigns_count } = row.original;
                        const textAlign =
                          cell.column.columnDef.textAlign || 'left';
                        const minWidth = cell.column.columnDef.minWidth || null;

                        return (
                          <Td
                            className="templates-table__body-cell"
                            px={index === 0 ? '0px' : '15px'}
                            py="0px"
                            key={cell.id}
                            textAlign={textAlign}
                            minWidth={minWidth}
                          >
                            {index === 0 ? (
                              <Box className="templates-table__preview-container">
                                <Box
                                  className="templates-table__indicator"
                                  bg={`${
                                    campaigns_count > 0
                                      ? 'green.400'
                                      : 'gray.400'
                                  }`}
                                ></Box>
                                <Box className="templates-table__preview">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          table={table}
          data={templatesData}
          pagination={pagination}
          setPagination={setPagination}
          setSelectedRowId={setSelectedRowId}
        />
      </Box>
      {showModal(warningModal, setWarningModal)}
      <DesignImporter
        open={openDesignImporterModal}
        onClose={() => setOpenDesignImporterModal(false)}
        refresh={() => setRefreshSymbol(Symbol())}
      />
    </>
  );
};

export default Templates;
